import {
    ApiBaseService,
    type EnhancedFetch,
    type EnhancedFetchOptions,
    type PaginatedFetch,
    type PaginatedFetchOptions,
} from "./ApiBaseService";
import type { Process } from "~/models/Automation";
import type { ApiResponse } from "~/types/ApiResponse";
import { faEye } from "@fortawesome/pro-duotone-svg-icons";
import {
    faArrowsRetweet,
    faCommand,
    faRepeat,
} from "@fortawesome/pro-regular-svg-icons";
import { faBolt } from "@fortawesome/pro-solid-svg-icons";
import { faFastForward } from "@fortawesome/pro-light-svg-icons";
import ApiError from "~/plugins/errors/apiError";
import EventService from "~/services/EventService";

export default class ProcessService extends ApiBaseService {
    public list(
        options?: Partial<PaginatedFetchOptions>,
    ): PaginatedFetch<Process[]> {
        return this.paginate<Process[]>(
            this.route("api.processes.index", "/processes"),
            options as PaginatedFetchOptions,
        );
    }

    public find(
        id: number | (() => number),
        options?: EnhancedFetchOptions,
    ): EnhancedFetch<Process> {
        return this.enhancedFetch<Process>(
            () =>
                this.route("api.processes.show", "/processes/{process}", {
                    process: typeof id === "function" ? id() : id,
                }),
            options,
        );
    }

    public findRequest(id: number): Promise<ApiResponse<Process>> {
        return this.request(
            this.route("api.processes.show", "/processes/{process}", {
                process: id,
            }),
        );
    }

    public update(
        id: number,
        data: Partial<Process>,
    ): Promise<ApiResponse<Process>> {
        return this.request(
            this.route("api.processes.store", "/processes/{process}", {
                process: id,
            }),
            {
                method: "PATCH",
                body: data,
            },
        );
    }

    public findActivity(
        id: number,
        data: Partial<any>,
    ): Promise<ApiResponse<any>> {
        return this.request(
            this.route("api.activity_logs.show", "/activity_logs/{id}", {
                id,
            }),
            {
                method: "GET",
                body: data,
            },
        );
    }

    public delete(id: number): Promise<null> {
        return this.request(
            this.route("api.processes.destroy", "/processes/{process}", {
                process: id,
            }),
            {
                method: "DELETE",
            },
        );
    }

    public fastForward(id: number): Promise<null> {
        return this.request(
            this.route(
                "api.processes.fast-forward",
                "/processes/{process}/fast-forward",
                {
                    process: id,
                },
            ),
            {
                method: "POST",
            },
        );
    }

    public dropdown(
        process: Process,
        options: Partial<{
            onFastForwarded: () => void;
        }> = {},
    ) {
        const items = [
            {
                label: "View",
                icon: faEye,
                to: {
                    name: "settings-runs-id",
                    params: { id: process.id },
                },
            },
        ];

        if (process.sub_state === "waiting") {
            items.push({
                label: "Fast Forward",
                icon: faFastForward,
                click: () =>
                    useToast().add({
                        id: `process:${process.id}:fast-forward`,
                        title: "Are you sure?",
                        description:
                            "Fast forwarding skips the delay and continues the automation.",
                        icon: "i-heroicons-question-mark-circle-16-solid",
                        timeout: 10000,
                        actions: [
                            {
                                label: "Yes, fast forward",
                                color: "primary",
                                variant: "solid",
                                click: () => {
                                    useToast().remove(
                                        `process:${process.id}:fast-forward`,
                                    );
                                    this.fastForward(process.id)
                                        .then(() => {
                                            useToast().add({
                                                title: "Run Fast Forwarded",
                                            });

                                            options.onFastForwarded?.();
                                        })
                                        .catch((e: ApiError) => {
                                            useToast().add({
                                                ...e.errors,
                                                title: e.message,
                                                icon: "i-heroicons-exclamation-triangle",
                                                color: "red",
                                            });
                                        });
                                },
                            },
                            {
                                label: "No",
                                color: "white",
                                variant: "solid",
                                click: () =>
                                    useToast().remove(
                                        `process:${process.id}:fast-forward`,
                                    ),
                            },
                        ],
                    }),
            });
        }

        const related = [];

        if (process.automation?.uuid) {
            related.push({
                label: "View Automation",
                icon: faArrowsRetweet,
                to: {
                    name: "automations-id",
                    params: { id: process.automation.uuid },
                },
            });
        }

        if (process.event?.id) {
            related.push({
                label: "View Event",
                icon: faBolt,
                to: {
                    name: "settings-events-id",
                    params: { id: process.event.id },
                },
            });

            if (
                process.event.custom_app?.uuid ||
                process.event.trigger_connection?.uuid
            ) {
                related.push({
                    label: "View App",
                    icon: faCommand,
                    to: {
                        name: "apps-id",
                        params: {
                            id:
                                process.event.custom_app?.uuid ??
                                process.event.trigger_connection?.uuid,
                        },
                    },
                });
            }

            if (
                process.event.custom_app?.uuid &&
                process.event.custom_app_trigger?.uuid
            ) {
                related.push({
                    label: "View Trigger",
                    icon: faBolt,
                    to: {
                        name: "apps-id-edit",
                        params: {
                            id: process.event.custom_app?.uuid,
                        },
                    },
                });
            }

            related.push({
                label: "Replay Event",
                icon: faRepeat,
                click: () => this.events().replay(process.event.id),
            });
        }

        if (useRoute().name === items[0].to.name) {
            items.splice(0, 1);
        }

        const groups = [];

        if (items.length) {
            groups.push(items);
        }

        if (related.length) {
            if (items.length) items[0].label += ` Run`;
            groups.push([{ label: "Related", disabled: true }, ...related]);
        }

        return groups;
    }

    private events(): EventService {
        return new EventService(this.getConfig());
    }
}
